import { useEffect, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Plus, Pencil, Trash2, Search, Mail, Phone, GraduationCap } from "lucide-react";
import { ImageUpload } from "@/components/image-upload";
import { StorageAvatarImage } from "@/components/storage-image";
import { extractStoragePath } from "@/lib/storage";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/teachers")({
  head: () => ({ meta: [{ title: "Data Guru — MI Al-Munajah Admin" }] }),
  component: TeachersPage,
});

type Teacher = {
  id: string; name: string; subject: string | null;
  phone: string | null; email: string | null; photo_url: string | null;
};

const empty = { name: "", subject: "", phone: "", email: "", photo_url: null as string | null };

function TeachersPage() {
  const [rows, setRows] = useCachedState<Teacher[]>("teachers:rows", []);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Teacher | null>(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await supabase.from("teachers").select("*").order("name");
    setRows((data || []) as Teacher[]);
  };

  useEffect(() => {
    load();
    const ch = supabase.channel("teachers-rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "teachers" }, load).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (t: Teacher) => {
    setEditing(t);
    setForm({
      name: t.name, subject: t.subject || "", phone: t.phone || "",
      email: t.email || "", photo_url: extractStoragePath("avatars", t.photo_url),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Nama guru wajib diisi");
    const { error } = editing
      ? await supabase.from("teachers").update(form).eq("id", editing.id)
      : await supabase.from("teachers").insert(form);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Guru diperbarui" : "Guru ditambahkan");
    setOpen(false);
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("teachers").delete().eq("id", id);
    if (error) return toast.error(error.message); else toast.success("Guru dihapus");
  };

  const filtered = rows.filter((r) =>
    !search || `${r.name} ${r.subject || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader
        title="Data Guru"
        description={`${rows.length} guru terdaftar`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startNew} className="rounded-xl"><Plus className="w-4 h-4 mr-2" /> Tambah Guru</Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Guru" : "Tambah Guru"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5"><Label>Nama Guru *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Mata Pelajaran</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Nomor HP</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                </div>
                <div className="space-y-1.5"><Label>Foto</Label>
                  <ImageUpload bucket="avatars" storePathOnly value={form.photo_url} onChange={(url) => setForm({ ...form, photo_url: url })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
                <Button onClick={save} className="rounded-xl">{editing ? "Simpan" : "Tambah"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="p-4 rounded-2xl mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama atau mapel..." className="pl-9 rounded-xl" />
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.length === 0 && <div className="col-span-full text-center text-muted-foreground py-16">Belum ada guru</div>}
        {filtered.map((t) => (
          <Card key={t.id} className="p-5 rounded-2xl hover:shadow-[var(--shadow-elevation-2)] transition-shadow">
            <div className="flex items-start gap-3">
              <Avatar className="w-14 h-14">
                {t.photo_url && <StorageAvatarImage bucket="avatars" value={t.photo_url} />}
                <AvatarFallback className="bg-primary/15 text-primary font-semibold">{t.name.split(" ").map(x=>x[0]).slice(0,2).join("")}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{t.name}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><GraduationCap className="w-3 h-3" /> {t.subject || "-"}</div>
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-3.5 h-3.5" /> {t.phone || "-"}</div>
              <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-3.5 h-3.5" /> <span className="truncate">{t.email || "-"}</span></div>
            </div>
            <div className="mt-4 flex justify-end gap-1">
              <Button variant="ghost" size="icon" onClick={() => startEdit(t)}><Pencil className="w-4 h-4" /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="ghost" size="icon"><Trash2 className="w-4 h-4 text-destructive" /></Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Hapus guru?</AlertDialogTitle><AlertDialogDescription>Tindakan tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => remove(t.id)}>Hapus</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
